import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Clock, Users, ChefHat, Bookmark, Filter, ChevronDown } from 'lucide-react';
import { LogoSmall } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecipesPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchRecipes();
    fetchCategories();
  }, [selectedCategory, selectedDifficulty]);

  const fetchRecipes = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedDifficulty) params.append('difficulty', selectedDifficulty);
      if (search) params.append('search', search);
      
      const res = await axios.get(`${API_URL}/api/recipes?${params.toString()}`);
      setRecipes(res.data.recipes || []);
    } catch (e) {
      console.error('Error fetching recipes:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/recipes/categories`);
      setCategories(res.data || []);
    } catch (e) {
      console.error('Error fetching categories:', e);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecipes();
  };

  const featuredRecipes = recipes.filter(r => r.featured);
  const regularRecipes = recipes.filter(r => !r.featured);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Hero */}
      <section className="pt-36 pb-12 bg-gradient-to-b from-[#dbeef5] to-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[#003456]/10 px-4 py-2 rounded-md mb-6">
            <ChefHat className="h-5 w-5 text-[#003456]" />
            <span className="text-[#003456] font-medium">Freeze Dried Recipe Collection</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0a0203] mb-6" style={{  }}>
            Delicious Recipes
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Discover amazing recipes using freeze-dried ingredients. From quick camping meals to gourmet dishes - save your favorites!
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input 
                type="text" 
                placeholder="Search recipes..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 py-6 text-lg rounded-full border-gray-200"
                data-testid="recipe-search"
              />
            </div>
            <Button type="submit" className="bg-[#003456] hover:bg-[#002B47] px-8 rounded-full" data-testid="recipe-search-btn">
              Search
            </Button>
          </form>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-gray-200 bg-white sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-gray-600 hover:text-[#003456]"
              >
                <Filter className="h-5 w-5" />
                Filters
                <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Quick category filters */}
              <div className="hidden md:flex items-center gap-2">
                <button 
                  onClick={() => setSelectedCategory('')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${!selectedCategory ? 'bg-[#003456] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  All
                </button>
                {categories.slice(0, 5).map(cat => (
                  <button 
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors capitalize ${selectedCategory === cat ? 'bg-[#003456] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            
            <p className="text-gray-500 text-sm">{recipes.length} recipes</p>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select 
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat} className="capitalize">{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                <select 
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="">All Levels</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Recipes Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003456] border-t-transparent"></div>
            </div>
          ) : recipes.length === 0 ? (
            <div className="text-center py-20">
              <ChefHat className="h-16 w-16 mx-auto mb-6 text-gray-300" />
              <h2 className="text-2xl font-bold text-[#0a0203] mb-2" style={{  }}>
                No recipes found
              </h2>
              <p className="text-gray-600 mb-6">Check back soon for delicious freeze-dried recipes!</p>
              <Button onClick={() => {setSearch(''); setSelectedCategory(''); setSelectedDifficulty('');}}>
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              {/* Featured Recipes */}
              {featuredRecipes.length > 0 && !search && !selectedCategory && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-[#0a0203] mb-6" style={{  }}>
                    Featured Recipes
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {featuredRecipes.map(recipe => (
                      <RecipeCard key={recipe.id} recipe={recipe} />
                    ))}
                  </div>
                </div>
              )}

              {/* All Recipes */}
              <div>
                {featuredRecipes.length > 0 && !search && !selectedCategory && (
                  <h2 className="text-2xl font-bold text-[#0a0203] mb-6" style={{  }}>
                    All Recipes
                  </h2>
                )}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {(search || selectedCategory ? recipes : regularRecipes).map(recipe => (
                    <RecipeCard key={recipe.id} recipe={recipe} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#003456] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{  }}>
            Ready to Start Freeze Drying?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Get your own D-Freeze freeze dryer and start creating delicious preserved meals.
          </p>
          <Link to="/products">
            <Button className="bg-white text-[#003456] hover:bg-gray-100 px-8 py-6 text-lg" data-testid="recipes-shop-cta">
              Shop Freeze Dryers
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RecipesPage;
